import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

// ProfilePicture component with loading and error states
const ProfilePicture = ({ src, alt, className = "", fallbackUrl = "https://res.cloudinary.com/dez2cotie/image/upload/v1748006632/Pics2_jfez90.webp" }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src || fallbackUrl);

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setCurrentSrc(src || fallbackUrl);
  }, [src, fallbackUrl]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    if (currentSrc !== fallbackUrl) {
      setCurrentSrc(fallbackUrl);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gray-700 rounded-full animate-pulse flex items-center justify-center">
          <i className="fas fa-user text-gray-500 text-sm"></i>
        </div>
      )}
      <img 
        src={currentSrc}
        alt={alt}
        className={`h-full w-full object-cover transition-opacity duration-200 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      {src && src !== fallbackUrl && (
        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full" 
             title="Profile picture from Google"></div>
      )}
    </div>
  );
};

function TopVoters() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [topVoters, setTopVoters] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTopVoters = async () => {
      setLoading(true);
      try {
        const response = await fetch('/.netlify/functions/leaderboard?type=voters');
        if (!response.ok) {
          throw new Error('Failed to fetch top voters data');
        }
        const data = await response.json();
        console.log('Top voters data received:', data);
        setTopVoters(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching top voters:', error);
        setError('Failed to load top voters. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTopVoters();
  }, []);

  // Get rank styling
  const getRankStyling = (index) => {
    switch (index) {
      case 0:
        return {
          bgClass: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
          textClass: 'text-black',
          icon: '👑'
        };
      case 1:
        return {
          bgClass: 'bg-gradient-to-r from-gray-300 to-gray-500',
          textClass: 'text-black',
          icon: '🥈'
        };
      case 2:
        return {
          bgClass: 'bg-gradient-to-r from-amber-600 to-amber-800',
          textClass: 'text-white',
          icon: '🥉'
        };
      default:
        return {
          bgClass: 'bg-cyan-500',
          textClass: 'text-white',
          icon: null
        };
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Page Header */}
      <div className="bg-gradient-to-b from-[#121214] to-black py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300 drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]" style={{ fontFamily: 'cursive, Arial, sans-serif' }}>
            🏆 Top Voters
          </h1>
          <p className="text-gray-300 text-center mt-4 max-w-2xl mx-auto">
            {t('discoverActiveMembers')}
          </p>
          {!loading && !error && topVoters.length > 0 && (
            <p className="text-cyan-400 text-center mt-2 text-sm">
              {topVoters.length} {topVoters.length > 1 ? t('votersCountPlural') : t('votersCount')} • {t('updatedRealTime')}
            </p>
          )}
        </div>
      </div>

      {/* Top Voters List */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-[#121214] border border-[#232329] rounded-lg shadow-lg shadow-cyan-500/5 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-32">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
              </div>
            ) : error ? (
              <div className="text-center py-32">
                <div className="text-6xl mb-4">😔</div>
                <p className="text-red-500 mb-4">{error}</p>
                <button 
                  className="mt-4 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-2 rounded-md transition duration-300"
                  onClick={() => window.location.reload()}
                >
                  {t('retryButton')}
                </button>
              </div>
            ) : topVoters.length === 0 ? (
              <div className="text-center py-32">
                <div className="text-6xl mb-4">🗳️</div>
                <p className="text-gray-400 mb-2">{t('noVotersYet')}</p>
                <p className="text-gray-500 text-sm">{t('beFirstToVote')}</p>
              </div>
            ) : (
              <>
                <div className="bg-[#18181B] py-4 px-6">
                  <div className="grid grid-cols-12 text-gray-200 font-medium">
                    <div className="col-span-2 text-center">{t('rank')}</div>
                    <div className="col-span-6">{t('userColumn')}</div>
                    <div className="col-span-4 text-center">{t('votes')}</div>
                  </div>
                </div>
                <div className="divide-y divide-[#232329]">
                  {topVoters.map((voter, index) => {
                    const rankStyle = getRankStyling(index);
                    return (
                      <div key={voter.id || index} className="grid grid-cols-12 items-center py-4 px-6 hover:bg-[#1c1c20] transition-colors duration-200">
                        <div className="col-span-2 flex justify-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${rankStyle.bgClass} ${rankStyle.textClass} shadow-lg`}>
                            {rankStyle.icon || (index + 1)}
                          </div>
                        </div>
                        <div className="col-span-6 flex items-center">
                          <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-cyan-500 relative">
                            <ProfilePicture 
                              src={voter.picture}
                              alt={voter.name || 'User'}
                              className="h-full w-full"
                            />
                          </div>
                          <div className="ml-3">
                            <span className="text-white font-medium italic block">
                              {voter.name || 'Anonymous User'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-4 text-center">
                          <div className="inline-flex items-center">
                            <span className="bg-cyan-500 text-white px-3 py-1 rounded-md font-medium">
                              {voter.vote_count || 0}
                            </span>
                            {voter.vote_count > 0 && (
                              <span className="ml-2 text-gray-400 text-sm">
                                vote{voter.vote_count > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Stats Footer */}
                <div className="bg-[#18181B] py-3 px-6 border-t border-[#232329]">
                  <div className="flex justify-between items-center text-sm text-gray-400">
                    <span>{t('totalVoters')}: {topVoters.length} {topVoters.length > 1 ? t('votersCountPlural') : t('votersCount')}</span>
                    <span>
                      Total votes: {topVoters.reduce((sum, voter) => sum + (voter.vote_count || 0), 0)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Back button */}
          <div className="text-center mt-12">
            <Link 
              to="/" 
              className="inline-flex items-center bg-cyan-500 hover:bg-cyan-600 text-black font-bold py-3 px-6 rounded-md transition duration-300 shadow-lg hover:shadow-cyan-500/25"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              {t('backToHome')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TopVoters;
